<template>
    <div class="relative">
        <div class="mt-4 w-full flex flex-wrap gap-2 justify-between items-center select-none"
             :class="[selectedContact ? '' : 'justify-between']">
            <div class="w-full flex items-center justify-between">
                <div class="flex flex-wrap items-center gap-2">
                    <h1 class="text-2xl font-light text-gray-400 whitespace-nowrap mr-4">{{ selectedContact ? selectedContact.full_name : title }}</h1>
                    <!--Text Buttons / Aligned Left-->
                    <div v-if="selectedContact" class="flex items-center gap-2">
                        <button class="gray-menu-button" @click.prevent="showEditContactForm">Edit</button>
                        <button class="gray-menu-button" @click.prevent="makePrimary(selectedContact)"
                                v-if="selectedContact.pivot.primary != true">Make primary
                        </button>
                        <button class="gray-menu-button"
                                @click.prevent="toggleStatusForRole(selectedContact)">
                            {{ selectedContact.pivot.status === "archived" ? "Restore" : "Archive" }}
                        </button>
                        <button v-if="showArchivedContacts && selectedContact.pivot.status == 'archived'"
                                class="gray-menu-button"
                                @click.prevent="removeContact(selectedContact)">Unlink
                        </button>
                    </div>
                </div>

                <!--Email and SMS icon buttons / Aligned Right-->
                <div v-if="selectedContact" class="flex items-center gap-2">
                    <button class="gray-menu-button" @click.prevent="createEmail(selectedContact.id)">
                        <icon-email class="w-5 h-5"></icon-email>
                    </button>
                    <button class="gray-menu-button" @click.prevent="smsContact(selectedContact)">
                        <icon-device-mobile class="w-5 h-5"></icon-device-mobile>
                    </button>
                </div>

                <!--Add Contact Icon-->
                <div v-else class="flex">
                    <button class="gray-menu-button" @click.prevent="toggleAddContactModal">
                        <icon-user-add class="h-4 w-4"></icon-user-add>
                    </button>
                </div>

            </div>
        </div>

        <div v-if="mutableContacts.length > 0" class="flow-root">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <div class="overflow-hidden shadow rounded-md">
                        <table ref="contactsTable" class="min-w-full divide-y divide-gray-300" :style="{ height: tableHeight + 'px' }">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th class="table-cell" style="width: 20px !important;">

                                    </th>
                                    <th class="py-2 px-2 text-left text-sm font-medium text-gray-900">
                                        Name
                                    </th>
                                    <th class="hidden md:table-cell px-3 py-2 text-left text-sm font-medium text-gray-900">
                                        Email
                                    </th>
                                    <th class="hidden md:table-cell  px-3 py-2 text-left text-sm font-medium text-gray-900">
                                        Mobile
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white" ref="rowDropdownMenuAnchor">
                                <contact-row-new
                                    :key="contact.id" v-for="contact in mutableContacts"
                                    :role="role"
                                    :contact="contact"
                                    @make-primary="makePrimary"
                                    @contact-updated="handleContactUpdatedEvent"
                                    @selection-changed="toggleContactMenu"
                                    :selected-id="selectedContact ? selectedContact.id : null">
                                </contact-row-new>

                                <tr v-if="mutableContacts && currentTailwindBreakpoint >= 2" v-for="i in emptyRows" :key="'empty-' + i" class="invisible">
                                    <td class="py-2 text-white"></td>
                                    <td class="hidden md:table-cell py-2 text-white">X</td>
                                    <td class="hidden md:table-cell py-2 text-white">X</td>
                                    <td class="hidden md:table-cell py-2 text-white">X</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="flex justify-center border-t border-gray-200"
                             v-if="mutableContacts && (paginationData.next_page_url || paginationData.prev_page_url)">
                            <plain-pagination
                                :meta="paginationData"
                                padding="2"
                                @page-changed="fetchContacts">
                            </plain-pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="!isLoadingContacts" class="select-none text-center flex items-center justify-center text-light text-3xl text-gray-400" style="height: 100px">
            <span>No {{ role }}s linked</span>
        </div>

        <div v-else class="flex items-center justify-center my-8">
            <loading-indicator></loading-indicator>
        </div>

        <create-or-link-contact-modal :show-modal="showAddContactModal"
                                      :role="role"
                                      :contactable="contactable"
                                      :contactable-id="contactableId"
                                      @contact-created="handleContactCreatedEvent"
                                      @contact-linked="handleContactLinkedEvent"
                                      @close="toggleAddContactModal">
        </create-or-link-contact-modal>

        <contact-view-edit
            v-if="selectedContact"
            ref="editContact"
            :contact="selectedContact"
            :hide-juristic-seciton="false"
            :role="role"
            :contactable="contactable"
            :contactable-id="contactableId"
            @contact-updated="handleContactUpdatedEvent"
        >
        </contact-view-edit>

        <simple-modal :show="!!(emailThread && emailThread.id)" max-width="sm:max-w-3xl" @close="emailThread = {}"
                      :title="!!(emailThread && emailThread.draft_email) ? '' : 'Viewing Email'" title-class="font-normal"
                      :body-class="!!(emailThread && emailThread.draft_email) ? '' : 'px-2 py-4'"
                      :show-title="!(emailThread && emailThread.draft_email)">
            <view-thread :thread="emailThread"
                         :context="emailContext"
                         v-if="userHasMailbox"
                         @close="emailThread = {}"
                         @forward-thread="threadForwardCreated"
                         @clear-context="clearEmailContext">
            </view-thread>
        </simple-modal>

    </div>

</template>

<script>

import PlainPagination from "../library/PlainPagination.vue";
import ContactRowNew from "./ContactRowNew.vue";
import CreateOrLinkContactModal from "../contacts/CreateOrLinkContactModal.vue";
import ComposeEmail from "../../mixins/compose-mail";
import createNewSms from "../sms/create-new-sms.vue";
import TableHeightManagementMixin from "../../mixins/table-height-management-mixin";
import SimplePaginatorMixin from "../../mixins/simple-paginator-mixin";

export default {
    name: "contacts-list-new",
    mixins: [ComposeEmail, TableHeightManagementMixin, SimplePaginatorMixin],
    components: {
        CreateOrLinkContactModal,
        ContactRowNew,
        PlainPagination,
    },
    props: {
        title: {
            type: String,
            required: true
        },
        subtitle: {
            type: String,
            required: false
        },
        contacts: {
            type: Array,
            required: false
        },
        showArchivedContacts: {
            type: Boolean,
            default: false
        },
        uri: {
            type: String,
            required: true
        },
        role: {
            type: String,
            required: true
        },
        contactable: {
            type: String,
            required: true
        },
        contactableId: {
            type: Number,
            required: true
        },
        allowCreate: {
            type: Boolean,
            default: false
        },
        selectContactText: {
            type: String,
            default: "Select Contact"
        },
    },

    data () {
        return {
            mutableContacts: [],
            currentContact: {},
            isLoadingContacts: true,
            showAddContactModal: false,
            selectedContact: null
        };
    },

    created: function () {
        let self = this;

        Bus.$on("new-contacts-available", function (role = null) {
            if (!role || role === self.role) {
                self.fetchContacts();
            }
        });

        Bus.$on("contact-deleted", function (role) {
            if (role === self.role) {
                self.fetchContacts();
            }
        });
    },

    mounted () {
        this.fetchContacts();
    },

    watch: {
        showArchivedContacts () {
            this.fetchContacts();
        }
    },

    computed: {
        createNewSms () {
            return createNewSms;
        },
    },

    methods: {
        smsContact (contact) {
            Bus.$emit("show-sms-create-form", contact);
        },

        toggleAddContactModal () {
            this.showAddContactModal = !this.showAddContactModal;
        },

        toggleContactMenu (bool, contact) {
            this.selectedContact = bool ? contact : null;
        },

        showEditContactForm () {
            this.$refs.editContact.setRole(this.role);
            this.$refs.editContact.toggleContactForm();
        },

        handleContactCreatedEvent () {
            this.fetchContacts();
        },

        handleContactLinkedEvent () {
            this.fetchContacts();
        },

        handleContactUpdatedEvent (contact) {
            if (!contact.pivot && this.selectedContact.pivot) {
                contact.pivot = this.selectedContact.pivot;
            }

            let index = this.mutableContacts.findIndex(c => c.id === contact.id);
            if (index !== -1) {
                this.mutableContacts.splice(index, 1, contact);
            }
            if (this.selectedContact && this.selectedContact.id === contact.id) {
                this.$set(this, "selectedContact", contact);
            }
        },

        toggleStatusForRole (contact) {
            App.post("/contacts/" + contact.id + "/update/status", new AppForm({
                role: contact.pivot.role,
                contactable: this.contactable,
                contactable_id: this.contactableId,
                currentStatus: contact?.pivot?.status
            })).then(() => {
                let index = this.mutableContacts.findIndex(c => c.id === contact.id);
                contact.pivot.status = contact.pivot.status === "archived" ? "active" : "archived";
                this.mutableContacts.splice(index, 1, contact);
                toastr.success("Contact Status Updated");
                this.clearSelectedContact();
                if (this.paginationData.currentPage === 1) {
                    this.fetchContacts();
                }
            });
        },

        clearSelectedContact () {
            this.selectedContact = null;
        },

        fetchContacts (paginationSettings = null) {
            this.isLoadingContacts = true;

            let page = 1;
            let perPage = 5;

            if (paginationSettings) {
                page = new URL(paginationSettings).searchParams.get("page");
            }

            axios.get(this.uri, {
                params: {
                    role: this.role,
                    page: page,
                    per_page: perPage,
                    show_archived: this.showArchivedContacts
                }
            }).then(response => {
                this.$set(this, "mutableContacts", response.data.data);
                this.setPaginatorData(response.data);
                this.setMaxItemsPreviouslyLoaded();
            }).catch(errors => {
                toastr.error("Error retrieving " + this.role + " contacts.");
            }).finally(() => {
                this.isLoadingContacts = false;
            });
        },

        makePrimary (contact) {
            App.post("/contacts/" + contact.id + "/primary", new AppForm({
                role: this.role,
                contactable: this.contactable,
                contactable_id: this.contactableId,
            })).then(() => {
                toastr.success("New primary " + this.role + " contact set");
                this.fetchContacts();
                this.clearSelectedContact();
            }).catch(() => {
                window.toastr.error("Could not set contacts as primary " + this.role);
            });
        },

        removeContact (contact) {
            let index = this.mutableContacts.indexOf(contact);
            App.patch("/contacts/" + contact.id + "/remove", new AppForm({
                role: this.role,
                contactable: this.contactable,
                contactable_id: this.contactableId
            })).then(() => {
                toastr.success("Contact removed from list");
                if (this.mutableContacts.length === 1) {
                    if (this.paginationData.currentPage > 1) {
                        this.paginationData.currentPage--;
                    }
                    this.fetchContacts();
                }
                this.mutableContacts.splice(index, 1);
                this.clearSelectedContact();
            }).catch(() => {
                window.toastr.error("Could not remove " + this.role + " contact!");
            });
        },

        sendEmail (contact) {
            this.$emit("create-email", contact);
        },

        sendSMS (contact) {
            Bus.$emit("show-sms-create-form", contact);
        },
    },
};
</script>

<style>
.gray-menu-button {
    @apply whitespace-nowrap inline-flex text-left w-full outline-none transition duration-300 rounded-md justify-center items-center border border-gray-300 shadow-sm
    bg-white font-normal text-gray-700 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100
    focus:ring-indigo-600 px-2 py-1 justify-center;
}
</style>
